import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, ArrowRight, Calendar, Tag, Loader2, Share2, Facebook, Twitter, Linkedin, Link as LinkIcon, Clock } from "lucide-react";
import { toast } from "sonner";
import { DOCTOR } from "../data/content";
import SEO from "../components/SEO";

export default function Articulo() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [a, r] = await Promise.all([
          api.get(`/articles/${slug}`),
          api.get(`/articles/${slug}/related?limit=3`),
        ]);
        setArticle(a.data);
        setRelated(r.data);
      } catch (e) {
        navigate("/blog", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  if (loading) {
    return <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto text-cyan-600" size={36} /></div>;
  }
  if (!article) return null;

  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareText = encodeURIComponent(article.title);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const readingTime = Math.max(1, Math.round(article.content.split(/\s+/).length / 200));

  return (
    <article>
      <SEO
        title={article.title}
        description={article.excerpt || article.title}
        canonical={`/articulos/${article.slug}`}
        image={article.featured_image}
        type="article"
      />
      {/* Hero */}
      {article.featured_image && (
        <section className="relative h-[360px] md:h-[480px] overflow-hidden">
          <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/30" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-4xl mx-auto px-4 pb-10 md:pb-14 w-full">
              <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 text-sm">
                <ArrowLeft size={16} /> Blog
              </Link>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500 text-white text-xs font-semibold">
                  <Tag size={12} /> {article.category}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-medium border border-white/20">
                  <Clock size={12} /> {readingTime} min lectura
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </section>
      )}

      {!article.featured_image && (
        <div className="max-w-4xl mx-auto px-4 pt-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-700 mb-6 text-sm"><ArrowLeft size={16} /> Blog</Link>
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 leading-tight mb-4">{article.title}</h1>
        </div>
      )}

      <section className="max-w-4xl mx-auto px-4 py-10 md:py-14">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-8 pb-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-bold">
              {article.author?.[0] || "C"}
            </div>
            <div>
              <div className="font-semibold text-slate-900">{article.author}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1"><Calendar size={12} />
                  {article.published_at && new Date(article.published_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 mr-1 hidden sm:inline">Compartir:</span>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-600 flex items-center justify-center transition" aria-label="Compartir en Facebook">
              <Facebook size={15} />
            </a>
            <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-black hover:text-white text-slate-600 flex items-center justify-center transition" aria-label="Compartir en Twitter/X">
              <Twitter size={15} />
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-blue-700 hover:text-white text-slate-600 flex items-center justify-center transition" aria-label="Compartir en LinkedIn">
              <Linkedin size={15} />
            </a>
            <a href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-green-500 hover:text-white text-slate-600 flex items-center justify-center transition" aria-label="Compartir en WhatsApp">
              <Share2 size={15} />
            </a>
            <button onClick={copyLink} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-cyan-600 hover:text-white text-slate-600 flex items-center justify-center transition" aria-label="Copiar enlace">
              <LinkIcon size={15} />
            </button>
          </div>
        </div>

        <p className="text-xl text-slate-700 italic leading-relaxed mb-8 pl-4 border-l-4 border-cyan-500">
          {article.excerpt}
        </p>

        <div
          className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-a:text-cyan-700 prose-blockquote:border-cyan-500 prose-blockquote:bg-cyan-50/60 prose-blockquote:py-1 prose-blockquote:px-5 prose-blockquote:rounded-r-lg prose-img:rounded-xl prose-img:shadow-md prose-strong:text-slate-900"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-slate-200 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-slate-500">Tags:</span>
            {article.tags.map((t) => (
              <Link key={t} to={`/blog?q=${encodeURIComponent(t)}`} className="text-xs bg-slate-100 hover:bg-cyan-100 hover:text-cyan-800 text-slate-600 px-3 py-1 rounded-full transition">
                #{t}
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-cyan-600 to-teal-700 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">¿Tienes dudas sobre este tema?</h3>
          <p className="text-cyan-50 mb-5">Consulta con {DOCTOR.shortName}, psiquiatra con {DOCTOR.yearsExperience} años de experiencia.</p>
          <Link to="/contacto" className="inline-block px-6 py-3 rounded-full bg-white text-cyan-700 font-semibold hover:scale-105 transition">
            Agendar consulta
          </Link>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-14">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Artículos relacionados</h3>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.id} to={`/articulos/${r.slug}`} className="group">
                  {r.featured_image && (
                    <div className="aspect-video overflow-hidden rounded-xl mb-3">
                      <img src={r.featured_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <span className="text-xs text-cyan-600 font-semibold uppercase">{r.category}</span>
                  <h4 className="font-bold text-slate-900 mt-1 leading-tight group-hover:text-cyan-700 transition line-clamp-2">{r.title}</h4>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 text-center">
          <Link to="/blog" className="inline-flex items-center gap-2 text-cyan-700 hover:text-cyan-900 font-semibold">
            <ArrowLeft size={16} /> Volver al blog
          </Link>
        </div>
      </section>
    </article>
  );
}
